import { HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { ImagesService } from '@app/api/marketplace/services';
import { ImageUploadActions } from '@image-upload/reducers/image-upload.slice';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { ImageWrapperService } from '@shared/services/image-wrapper.service';

@Injectable()
export class ImageUploadEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly imagesService: ImagesService,
    private readonly imageWrapperService: ImageWrapperService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly store: Store
  ) {}

  uploadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageUploadActions.upload.trigger),
      switchMap((action) => {
        const formData: FormData = new FormData();
        formData.append('file', action.file, action.file.name);
        const blob = new Blob([JSON.stringify(action.request)], {
          type: 'application/json',
        });

        return this.imageWrapperService
          .createImageToEntity$Response({
            body: {
              file: formData.get('file') as Blob,
              imageCreateRequest: blob,
            },
          })
          .pipe(
            filter(
              (event: any) =>
                event.type === HttpEventType.UploadProgress ||
                event.type === HttpEventType.Response
            ),
            tap((event: any) => {
              if (event.type === HttpEventType.UploadProgress) {
                const progress = Math.round((100 / event.total) * event.loaded);
                this.store.dispatch(
                  ImageUploadActions.upload.progress({ progress })
                );
              }
            }),
            filter((event: any) => event.type === HttpEventType.Response),
            map((event: any) => {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant(
                  'SHARED.IMAGE_PICKER.SUCCESSFULLY_ATTACHED'
                ),
              });
              return ImageUploadActions.upload.success({ image: event.body });
            }),
            catchError(() => of(ImageUploadActions.upload.failure()))
          );
      })
    )
  );

  loadImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageUploadActions.loadImages.trigger),
      switchMap((action) =>
        this.imagesService.getImagesByTypeAndId(action.request).pipe(
          map((response) =>
            ImageUploadActions.loadImages.success({ images: response })
          ),
          catchError(() => of(ImageUploadActions.loadImages.failure()))
        )
      )
    )
  );

  updateImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageUploadActions.updateImageData.trigger),
      switchMap((action) =>
        this.imagesService
          .updateImageProperties({ id: action.imageId, body: action.request })
          .pipe(
            map((response) => {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant(
                  'SHARED.IMAGE_PICKER.SUCCESSFULLY_MODIFIED'
                ),
              });
              return ImageUploadActions.updateImageData.success({
                image: response,
              });
            }),
            catchError(() => of(ImageUploadActions.updateImageData.failure()))
          )
      )
    )
  );

  deleteImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageUploadActions.deleteImage.trigger),
      switchMap((action) =>
        this.imagesService.deleteImage1({ id: action.imageId }).pipe(
          map(() => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              duration: 3000,
              data: this.translateService.instant(
                'SHARED.IMAGE_PICKER.SUCCESSFULLY_DELETED'
              ),
            });
            return ImageUploadActions.deleteImage.success({
              id: action.imageId,
            });
          }),
          catchError(() => of(ImageUploadActions.deleteImage.failure()))
        )
      )
    )
  );
}
